import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import DonutChart from '../../../components/ui/DonutChart'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import { STATUS_TONE_COLOR_VAR } from '../../actions/types'
import type { DelegationSummary } from '../types'

interface Props {
  delegation: DelegationSummary
  hint?: ReactNode
  /**
   * Seçili kategori (`assignedToMe` | `delegated`); `null` → seçim yok. Yalnız {@link Props.onSelect} ile
   * birlikte anlamlıdır.
   */
  selectedKey?: string | null
  /**
   * Bir kategoriye tıklandı (Raporum revizyonu §2.3: "…tıklandığında Aksiyon Beklenen Maddeler
   * tablosunda ilgili aksiyonlar gösterilecektir"). Aynı kategoriye yeniden tıklamanın "seçimi kaldır"
   * olması ÇAĞIRANIN işidir.
   */
  onSelect?: (key: string) => void
  /** Grafiğin altındaki tek satırlık yönerge. */
  footNote?: ReactNode
}

/**
 * "Delegasyon Özeti" (068 — analiz §2.4, spec §K8): iki dilimli donut — **Bana Atanan** ve
 * **Devrettiklerim**.
 *
 * <p>İki kategori Raporum'un taban kümesini <b>tam olarak</b> ikiye böler, bu yüzden merkezdeki toplam
 * dilimlerin toplamına eşittir ve yüzdeler %100 eder. (Analizin mockup'ında 78 + 32 = 110 iken merkez 126
 * yazıyordu; o hâliyle donut geçersizdi — spec §K8.)
 *
 * <p>Renkler statü aile paletinden ({@link STATUS_TONE_COLOR_VAR}, panonun diğer kartlarıyla tek kaynak)
 * alınır: <b>Bana Atanan mavi</b> (`ongoing` — üzerimde duran aktif iş), <b>Devrettiklerim turuncu</b>
 * (`updated`). Turuncu 2026-08-19'da nötr grinin yerini aldı (Raporum revizyonu §2.3: "grafikte yalnızca
 * iki renk kullanılacaktır: Bana Atanan Mavi, Devrettiklerim Turuncu").
 *
 * <p><b>Seçim:</b> `onSelect` verildiğinde legend satırları gerçek `<button>` olur — klavye ve ekran
 * okuyucu yolu <b>burasıdır</b>; donut dilimi yalnız fare için bir kısayoldur (§1'in donut'uyla aynı
 * desen).
 */
export default function DelegationCard({
  delegation,
  hint,
  selectedKey = null,
  onSelect,
  footNote,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const title = t('home.my.delegation.title')

  if (delegation.total === 0) {
    return (
      <SectionCard title={title}>
        <EmptyState
          icon="pi pi-share-alt"
          title={t('home.my.delegation.emptyTitle')}
          description={t('home.my.delegation.emptyDesc')}
        />
      </SectionCard>
    )
  }

  const rows = [
    {
      key: 'assignedToMe',
      label: t('home.my.delegation.assignedToMe'),
      value: delegation.assignedToMeCount,
      percent: delegation.assignedToMePercent,
      color: STATUS_TONE_COLOR_VAR.ongoing,
    },
    {
      key: 'delegated',
      label: t('home.my.delegation.delegated'),
      value: delegation.delegatedCount,
      percent: delegation.delegatedPercent,
      color: STATUS_TONE_COLOR_VAR.updated,
    },
  ]

  const selectable = onSelect != null

  return (
    <SectionCard title={title} hint={hint} className="home-dist">
      <div className="home-dist-body">
        <DonutChart
          ariaLabel={title}
          total={delegation.total}
          totalLabel={t('home.distribution.total')}
          selectedKeys={selectedKey ? [selectedKey] : []}
          onSelect={onSelect}
          slices={rows.map((r) => ({ key: r.key, label: r.label, value: r.value, color: r.color }))}
        />
        {/* Legend biçimi StatusDistributionCard ile AYNI (077 §K3'te iki satıra geçti): iki donut aynı
            kartta yan yana durabildiği için düzenlerinin ayrışmaması gerekir. */}
        <ul className="home-dist-legend">
          {rows.map((r) => {
            const selected = selectedKey === r.key
            const content = (
              <>
                <span className="home-dist-dot" style={{ background: r.color }} aria-hidden="true" />
                <span className="home-dist-text">
                  <span className="home-dist-name" title={r.label}>
                    {r.label}
                  </span>
                  <span className="home-dist-stats">
                    <span className="home-dist-count">{r.value}</span>
                    <span className="home-dist-pct">(%{r.percent})</span>
                  </span>
                </span>
              </>
            )
            return (
              <li
                key={r.key}
                className={selected ? 'home-dist-item home-dist-item--on' : 'home-dist-item'}
              >
                {selectable ? (
                  <button
                    type="button"
                    className="home-dist-row home-dist-btn"
                    aria-pressed={selected}
                    onClick={() => onSelect(r.key)}
                  >
                    {content}
                  </button>
                ) : (
                  <span className="home-dist-row">{content}</span>
                )}
              </li>
            )
          })}
        </ul>
      </div>
      {footNote != null && <p className="home-dist-note">{footNote}</p>}
    </SectionCard>
  )
}
